// Platform leaderboard for /app/report. Sortable DataTable wired to the
// perPlatform array from /api/report. Inline share-of-voice bar in the
// Sessions column shows each platform's % of the section total.
//
// M13.4 — when an `ecommercePerPlatform` payload is supplied (probe-pass
// + windowActive), the leaderboard appends Revenue / Transactions / AOV
// / RPU columns joined by canonical platform key. The wrapping container
// scrolls horizontally on narrow screens so the extra columns don't
// crash the layout.

import { useMemo } from "react";
import { DataTable, type Column, type SortDirection } from "@/components/ui/data-table";
import { cn } from "@/lib/utils";
import type { ReportCompare, ReportEcommercePlatformRow, ReportPlatformRow } from "@/lib/api";
import {
  engagementRateInfo,
  keyEventsInfo,
  revenueInfo,
  sessionsInfo,
} from "@/components/report/metric-info";
import { PlatformBadge } from "@/components/report/PlatformBadge";
import { CurrencyValue } from "@/components/report/CurrencyValue";

interface PlatformLeaderboardProps {
  rows: ReportPlatformRow[];
  compare: ReportCompare;
  showRevenue: boolean;
  /** Passed through to the Conversions header tooltip so its deep-link
   *  lands on the user's actual GA4 Admin → Key events page. */
  propertyId?: string;
  /** M13.4 — ecommerce per-platform rows. When supplied (probe-pass +
   *  windowActive), the table appends Revenue / Txns / AOV / RPU
   *  columns joined by canonical platform key. */
  ecommercePerPlatform?: ReportEcommercePlatformRow[];
  /** ISO 4217 currency code for the new ecommerce cells. Null = render
   *  plain numbers (Admin API failure). */
  currencyCode?: string | null;
}

export function PlatformLeaderboard({
  rows,
  compare,
  showRevenue,
  propertyId,
  ecommercePerPlatform,
  currencyCode,
}: PlatformLeaderboardProps) {
  const totalSessions = useMemo(() => rows.reduce((acc, r) => acc + r.sessions, 0), [rows]);
  const compareActive = compare !== "none";

  // M13.4 — index ecommerce rows by platform key for O(1) lookup
  // inside the cell renderers.
  const ecommerceByPlatform = useMemo(() => {
    if (!ecommercePerPlatform || ecommercePerPlatform.length === 0) return null;
    return new Map(ecommercePerPlatform.map((p) => [p.platform, p] as const));
  }, [ecommercePerPlatform]);
  const showEcommerceColumns = ecommerceByPlatform !== null;

  const columns = useMemo<Column<ReportPlatformRow>[]>(() => {
    const base: Column<ReportPlatformRow>[] = [
      {
        id: "platform",
        header: "Platform",
        sortable: true,
        accessorKey: "platform",
        cell: (row) => <PlatformBadge platform={row.platform} />,
      },
      {
        id: "sessions",
        header: "Sessions",
        sortable: true,
        accessorKey: "sessions",
        info: sessionsInfo,
        cell: (row) => (
          <SessionsCell
            sessions={row.sessions}
            share={totalSessions > 0 ? row.sessions / totalSessions : 0}
            delta={compareActive ? row.delta.sessions : null}
          />
        ),
      },
      {
        id: "engagement",
        header: "Engaged %",
        sortable: true,
        info: engagementRateInfo,
        cell: (row) => {
          const rate = row.sessions > 0 ? row.engagedSessions / row.sessions : 0;
          return <span className="tabular-nums">{percent(rate)}</span>;
        },
      },
      {
        id: "conversions",
        header: "Conversions",
        sortable: true,
        accessorKey: "keyEvents",
        info: keyEventsInfo(propertyId),
        cell: (row) => (
          <span className="tabular-nums">
            {row.keyEvents.toLocaleString()}
            {compareActive ? <DeltaInline delta={row.delta.keyEvents} /> : null}
          </span>
        ),
      },
    ];

    if (showRevenue) {
      base.push({
        id: "revenue",
        header: "Revenue",
        sortable: true,
        accessorKey: "totalRevenue",
        info: revenueInfo,
        cell: (row) => (
          <span className="tabular-nums">
            {currency(row.totalRevenue)}
            {compareActive ? <DeltaInline delta={row.delta.totalRevenue} /> : null}
          </span>
        ),
      });
    }

    // M13.4 — ecommerce columns. Appended only when the parent passes
    // an `ecommercePerPlatform` block (probe-pass + windowActive). The
    // four columns (Ecom Revenue, Transactions, AOV, RPU) join by
    // canonical platform key — platforms with no ecommerce row render
    // an em-dash so the visual rhythm is preserved.
    if (showEcommerceColumns && ecommerceByPlatform) {
      base.push(
        {
          id: "ecomRevenue",
          header: "Ecom revenue",
          sortable: true,
          cell: (row) => {
            const e = ecommerceByPlatform.get(row.platform);
            if (!e) return <span className="text-neutral-400">—</span>;
            return (
              <span className="tabular-nums">
                <CurrencyValue value={e.revenue} currencyCode={currencyCode ?? null} />
                {compareActive ? <DeltaInline delta={e.deltas.revenue} /> : null}
              </span>
            );
          },
        },
        {
          id: "transactions",
          header: "Transactions",
          sortable: true,
          cell: (row) => {
            const e = ecommerceByPlatform.get(row.platform);
            if (!e) return <span className="text-neutral-400">—</span>;
            return (
              <span className="tabular-nums">
                {e.purchases.toLocaleString()}
                {compareActive ? <DeltaInline delta={e.deltas.purchases} /> : null}
              </span>
            );
          },
        },
        {
          id: "aov",
          header: "AOV",
          sortable: true,
          cell: (row) => {
            const e = ecommerceByPlatform.get(row.platform);
            if (!e) return <span className="text-neutral-400">—</span>;
            return (
              <span className="tabular-nums">
                <CurrencyValue value={e.averageOrderValue} currencyCode={currencyCode ?? null} />
              </span>
            );
          },
        },
        {
          id: "rpu",
          header: "Revenue / user",
          sortable: true,
          cell: (row) => {
            const e = ecommerceByPlatform.get(row.platform);
            if (!e) return <span className="text-neutral-400">—</span>;
            return (
              <span className="tabular-nums">
                <CurrencyValue value={e.revenuePerUser} currencyCode={currencyCode ?? null} />
              </span>
            );
          },
        },
      );
    }

    return base;
  }, [
    showRevenue,
    totalSessions,
    compareActive,
    propertyId,
    showEcommerceColumns,
    ecommerceByPlatform,
    currencyCode,
  ]);

  if (rows.length === 0) {
    return (
      <div className="rounded-xl bg-white p-8 text-center ring-1 ring-neutral-950/5">
        <p className="font-display text-base font-semibold text-neutral-950">
          No platforms in this period
        </p>
        <p className="mt-2 text-sm text-neutral-600 text-pretty">
          Once your social traffic starts arriving, each platform that drove sessions will appear
          here, sorted by impact.
        </p>
      </div>
    );
  }

  // M13.4 — wrap in a horizontal scroll container when ecommerce
  // columns are present so the extra 4 columns don't break narrow
  // screens. The non-ecommerce path stays unwrapped (preserves the
  // existing layout pixel-for-pixel).
  const table = (
    <DataTable<ReportPlatformRow>
      columns={columns}
      data={rows}
      getRowId={(r) => r.platform}
      defaultSortColumn="sessions"
      defaultSortDirection="desc"
      sortComparator={makeComparator(ecommerceByPlatform)}
    />
  );
  if (showEcommerceColumns) {
    return <div className="overflow-x-auto xl:overflow-visible">{table}</div>;
  }
  return table;
}

interface SessionsCellProps {
  sessions: number;
  share: number;
  delta: number | null;
}

function SessionsCell({ sessions, share, delta }: SessionsCellProps) {
  return (
    <span className="inline-flex flex-col items-start gap-1 tabular-nums">
      <span className="flex items-center gap-2">
        {sessions.toLocaleString()}
        {delta !== null ? <DeltaInline delta={delta} /> : null}
      </span>
      <span
        aria-hidden="true"
        className="block h-1 w-32 overflow-hidden rounded-full bg-neutral-100"
      >
        <span
          className="block h-full rounded-full bg-purple-500"
          style={{ width: `${Math.max(2, Math.round(share * 100))}%` }}
        />
      </span>
    </span>
  );
}

function DeltaInline({ delta }: { delta: number | null }) {
  if (delta === null) return <span className="ml-2 text-xs text-neutral-400">—</span>;
  const positive = delta >= 0;
  return (
    <span
      className={cn(
        "ml-2 inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums",
        positive ? "text-green-700" : "text-red-700",
      )}
    >
      {positive ? "+" : ""}
      {(delta * 100).toFixed(1)}%
    </span>
  );
}

/** Comparator factory — captures the ecommerce lookup so the
 *  M13.4 ecommerce columns can sort by their joined-row metric without
 *  threading the lookup through every call site. The factory layer
 *  keeps the existing DataTable contract unchanged. */
function makeComparator(ecommerceByPlatform: Map<string, ReportEcommercePlatformRow> | null) {
  return (
    a: ReportPlatformRow,
    b: ReportPlatformRow,
    column: string,
    direction: SortDirection,
  ): number => {
    if (!direction) return 0;
    const dir = direction === "asc" ? 1 : -1;
    switch (column) {
      case "platform":
        return a.platform.localeCompare(b.platform) * dir;
      case "sessions":
        return (a.sessions - b.sessions) * dir;
      case "engagement": {
        const aRate = a.sessions > 0 ? a.engagedSessions / a.sessions : 0;
        const bRate = b.sessions > 0 ? b.engagedSessions / b.sessions : 0;
        return (aRate - bRate) * dir;
      }
      case "conversions":
        return (a.keyEvents - b.keyEvents) * dir;
      case "revenue":
        return (a.totalRevenue - b.totalRevenue) * dir;
      case "ecomRevenue": {
        const av = ecommerceByPlatform?.get(a.platform)?.revenue ?? 0;
        const bv = ecommerceByPlatform?.get(b.platform)?.revenue ?? 0;
        return (av - bv) * dir;
      }
      case "transactions": {
        const av = ecommerceByPlatform?.get(a.platform)?.purchases ?? 0;
        const bv = ecommerceByPlatform?.get(b.platform)?.purchases ?? 0;
        return (av - bv) * dir;
      }
      case "aov": {
        const av = ecommerceByPlatform?.get(a.platform)?.averageOrderValue ?? 0;
        const bv = ecommerceByPlatform?.get(b.platform)?.averageOrderValue ?? 0;
        return (av - bv) * dir;
      }
      case "rpu": {
        const av = ecommerceByPlatform?.get(a.platform)?.revenuePerUser ?? 0;
        const bv = ecommerceByPlatform?.get(b.platform)?.revenuePerUser ?? 0;
        return (av - bv) * dir;
      }
      default:
        return 0;
    }
  };
}

const percent = (n: number) => `${(n * 100).toFixed(1)}%`;
const currency = (n: number) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
